
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const ClinicalSciencePage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-50 to-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Clinical Science
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Understanding photobiomodulation mechanisms and research foundations in recovery support applications
          </p>
        </div>
      </section>

      {/* Mechanism Overview Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Photobiomodulation Mechanism</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Cellular Level Interactions</h3>
              <ul className="space-y-3 text-gray-700">
                <li>• Light absorption by cellular chromophores</li>
                <li>• Mitochondrial cytochrome c oxidase activation</li>
                <li>• ATP (adenosine triphosphate) production enhancement</li>
                <li>• Cellular metabolic activity modulation</li>
              </ul>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Proposed Pathways</h3>
              <ul className="space-y-3 text-gray-700">
                <li>• Reactive oxygen species (ROS) modulation</li>
                <li>• Nitric oxide release mechanisms</li>
                <li>• Cellular signaling pathway activation</li>
                <li>• Gene expression modifications</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Wavelength Specifications Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Wavelength Specifications</h2>
          
          <div className="bg-white rounded-lg shadow-sm p-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">Commonly Studied Wavelength Ranges</h3>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="border-l-4 border-red-500 pl-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">Red Light Spectrum</h4>
                <div className="space-y-3">
                  <p className="text-2xl font-bold text-red-600">630–660nm</p>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Superficial tissue penetration</li>
                    <li>• Skin and surface applications</li>
                    <li>• Cellular metabolism studies</li>
                    <li>• Wound healing research focus</li>
                  </ul>
                </div>
              </div>
              
              <div className="border-l-4 border-purple-600 pl-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">Near-Infrared Spectrum</h4>
                <div className="space-y-3">
                  <p className="text-2xl font-bold text-purple-600">810–850nm</p>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Deeper tissue penetration</li>
                    <li>• Muscle and joint applications</li>
                    <li>• Sports medicine research</li>
                    <li>• Recovery and performance studies</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="mt-8 p-6 bg-blue-50 rounded-lg">
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Research Considerations</h4>
              <p className="text-gray-700">
                Optimal wavelength selection varies by target tissue depth, application area, and specific research objectives. 
                Many studies investigate combination protocols using both red and near-infrared wavelengths for synergistic effects.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Peer-Reviewed Research Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Peer-Reviewed Research</h2>
          
          <div className="bg-gray-50 p-8 rounded-lg">
            <p className="text-gray-600 mb-8">
              Selected research publications investigating photobiomodulation mechanisms and applications:
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Hamblin MR (2017)</h3>
                <p className="text-gray-700 mb-4">
                  "Mechanisms and applications of the anti-inflammatory effects of photobiomodulation"
                </p>
                <p className="text-sm text-gray-600">
                  <em>AIMS Biophysics</em> - Comprehensive review of cellular mechanisms and anti-inflammatory pathways
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Chung et al. (2012)</h3>
                <p className="text-gray-700 mb-4">
                  "The nuts and bolts of low-level laser (light) therapy"
                </p>
                <p className="text-sm text-gray-600">
                  <em>Annals of Biomedical Engineering</em> - Technical foundations and dosimetry considerations
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Zhang et al. (2023)</h3>
                <p className="text-gray-700 mb-4">
                  "Photobiomodulation therapy for sports injuries and recovery"
                </p>
                <p className="text-sm text-gray-600">
                  <em>Sports Medicine and Health Science</em> - Athletic performance and recovery applications
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Shamloo et al. (2023)</h3>
                <p className="text-gray-700 mb-4">
                  "Photobiomodulation in orthopedic and musculoskeletal conditions"
                </p>
                <p className="text-sm text-gray-600">
                  <em>Photomedicine and Laser Surgery</em> - Clinical applications in musculoskeletal care
                </p>
              </div>
            </div>
            
            <div className="mt-8 p-6 bg-teal-50 border border-teal-200 rounded-lg">
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Research Database Resources</h4>
              <p className="text-gray-700">
                Additional peer-reviewed publications are available through PubMed, PEDro (Physiotherapy Evidence Database), 
                and Cochrane Library using search terms: "photobiomodulation," "low-level laser therapy," "LLLT," and "red light therapy."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Research Areas Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Current Research Areas</h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Pain Management</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Chronic pain conditions</li>
                <li>• Post-operative pain</li>
                <li>• Musculoskeletal discomfort</li>
                <li>• Neuropathic pain studies</li>
              </ul>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Tissue Healing</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Wound healing acceleration</li>
                <li>• Soft tissue repair</li>
                <li>• Bone healing studies</li>
                <li>• Scar tissue management</li>
              </ul>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Sports Medicine</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Exercise recovery</li>
                <li>• Performance enhancement</li>
                <li>• Injury prevention</li>
                <li>• Muscle fatigue reduction</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-teal-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Explore Clinical Applications</h2>
          <p className="text-xl text-teal-100 mb-8">
            Learn how photobiomodulation systems integrate into clinical practice environments
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/clinical-applications" 
              className="bg-white text-teal-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors duration-200 whitespace-nowrap"
            >
              View Clinical Applications
            </a>
            <a 
              href="/contact" 
              className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-teal-600 transition-colors duration-200 whitespace-nowrap"
            >
              Request Research Summary
            </a>
          </div>
        </div>
      </section>

      {/* Disclaimer Section */}
      <section className="py-8 bg-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <p className="text-sm text-yellow-800">
              <strong>Educational Disclaimer:</strong> Information provided is for educational purposes only and is not medical advice. 
              Research findings do not constitute treatment recommendations or clinical guidance. Consult healthcare professionals 
              for medical decisions. Individual results may vary, and outcomes depend on multiple factors beyond photobiomodulation therapy.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ClinicalSciencePage;
